// Zone Plan API Service
// Handles saving and retrieving zone plans with sensor positions

const API_BASE = 'https://s3.ts1cloud.com/service3';

interface ZonePlanData {
  zoneId: number;
  planName: string;
  planDescription?: string;
  imageData: string; // Base64 encoded image
  imageFormat?: string;
  scale?: number;
  width?: number;
  height?: number;
  sensorPositions: SensorPositionData[];
}

interface SensorPositionData {
  sensorId: number;
  positionX: number;
  positionY: number;
  sensorSize?: number;
  rotation?: number;
  isVisible?: boolean;
  /** Manual label position on plan (px offset from pin tip). */
  labelCenterOffsetX?: number;
  labelCenterOffsetY?: number;
}

interface ZonePlanResponse {
  id: number;
  zoneId: number;
  planName: string;
  planDescription?: string;
  imageData: string;
  imageFormat: string;
  scale: number;
  width?: number;
  height?: number;
  sensorPositions: SensorPositionData[];
  createdAt: string;
  updatedAt: string;
  createdBy: number;
  isActive: boolean;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

/** Convert a siteplan field from the API (Buffer JSON or base64 string) to a base64 string. */
function toBase64(value: any): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  // node-pg / Workers may serialize Buffer as { type: 'Buffer', data: [...] }
  if (value.type === 'Buffer' && Array.isArray(value.data)) {
    const bytes = new Uint8Array(value.data);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
  }
  return '';
}

class ZonePlanApiService {
  private buildHeaders(user: any): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      'x-company-id': user?.companyID?.toString() || '',
      'x-company-name': user?.companyName || '',
    };
  }

  // Save zone plan to database via direct API
  async saveZonePlan(planData: ZonePlanData, user: any): Promise<ApiResponse<ZonePlanResponse>> {
    try {
      const rawBase64 = planData.imageData?.startsWith('data:')
        ? planData.imageData.split(',')[1]
        : planData.imageData;

      const body = {
        siteId: planData.zoneId,
        siteDescription: planData.planDescription || planData.planName,
        imageBuffer: rawBase64,
        scale: planData.scale ?? 1,
        sensorPositions: planData.sensorPositions,
        siteUsers: null,
        siteImage: null,
      };

      const response = await fetch(`${API_BASE}/sites`, {
        method: 'POST',
        credentials: 'include',
        headers: this.buildHeaders(user),
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(response.status === 403 ? 'Permission denied' : `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.status === 'success' || result.action) {
        return { success: true, message: 'Zone plan saved successfully' };
      }
      return { success: false, error: result.message || 'Failed to save zone plan' };
    } catch (error) {
      console.error('Error saving zone plan:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  // Retrieve zone plan from database via direct API
  async getZonePlan(zoneId: number, user: any): Promise<ApiResponse<ZonePlanResponse>> {
    try {
      const response = await fetch(`${API_BASE}/sites/${zoneId}/plan`, {
        method: 'GET',
        credentials: 'include',
        headers: this.buildHeaders(user),
      });

      if (response.status === 404) {
        return { success: false, error: 'No zone plan found for the specified zone' };
      }

      if (!response.ok) {
        throw new Error(response.status === 403 ? 'Permission denied' : `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const site = result.data ?? result;

      if (!site || (!site.siteplan && !site.imageBuffer)) {
        return { success: false, error: 'No zone plan found for the specified zone' };
      }

      const imageData = toBase64(site.siteplan ?? site.imageBuffer);

      const positions: SensorPositionData[] = (() => {
        const raw = site.positions ?? site.sensorPositions;
        if (!raw) return [];
        if (Array.isArray(raw)) return raw;
        try { return JSON.parse(raw); } catch { return []; }
      })();

      const converted: ZonePlanResponse = {
        id: site.siteId ?? zoneId,
        zoneId: site.siteId ?? zoneId,
        planName: site.siteDescription || '',
        planDescription: site.siteDescription,
        imageData,
        imageFormat: 'png',
        scale: site.scale ?? 1,
        sensorPositions: positions,
        createdAt: site.created_at || '',
        updatedAt: site.updated_at || '',
        createdBy: user?.id ?? 0,
        isActive: true,
      };

      return { success: true, data: converted, message: 'Zone plan retrieved successfully' };
    } catch (error) {
      console.error('Error retrieving zone plan:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  // Update sensor positions for existing zone plan
  async updateSensorPositions(
    zoneId: number,
    sensorPositions: SensorPositionData[],
    user: any
  ): Promise<ApiResponse<boolean>> {
    try {
      const body = {
        siteId: zoneId,
        sensorPositions,
      };

      const response = await fetch(`${API_BASE}/sites/${zoneId}/plan`, {
        method: 'PATCH',
        credentials: 'include',
        headers: this.buildHeaders(user),
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(response.status === 403 ? 'Permission denied' : `HTTP error! status: ${response.status}`);
      }

      return { success: true, data: true, message: 'Sensor positions updated successfully' };
    } catch (error) {
      console.error('Error updating sensor positions:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  // Lightweight: returns IDs of zones that have a site plan (no image data)
  async listZonesWithPlans(user: any): Promise<ApiResponse<number[]>> {
    try {
      const response = await fetch(`${API_BASE}/sites/with-plans`, {
        method: 'GET',
        credentials: 'include',
        headers: this.buildHeaders(user),
      });

      if (!response.ok) {
        throw new Error(response.status === 403 ? 'Permission denied' : `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const rows: { siteId?: number; siteid?: number }[] = result.data ?? result;
      const ids = rows.map(r => r.siteId ?? r.siteid ?? 0).filter(id => id > 0);
      // Dedupe preserves first-seen order; duplicates break slideshow advance (indexOf repeats same slot).
      return { success: true, data: [...new Set(ids)] };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  // Delete zone plan
  async deleteZonePlan(zoneId: number, user: any): Promise<ApiResponse<boolean>> {
    try {
      const response = await fetch(`${API_BASE}/sites/${zoneId}/plan`, {
        method: 'DELETE',
        credentials: 'include',
        headers: this.buildHeaders(user),
      });

      if (!response.ok) {
        throw new Error(response.status === 403 ? 'Permission denied' : `HTTP error! status: ${response.status}`);
      }

      return { success: true, data: true, message: 'Zone plan deleted successfully' };
    } catch (error) {
      console.error('Error deleting zone plan:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }
}

// Export the service
export { ZonePlanApiService, type ZonePlanData, type SensorPositionData, type ZonePlanResponse, type ApiResponse };
